/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { HttpParams } from "@angular/common/http";
import { of, throwError } from "rxjs";
import { ApplicationError } from "../../../../models/ui/application-error.model";
import { HttpRequestOptions } from "../../../../models/ui/http-request-options.model";
import {
	AI_BEST_10_EMISSIONS_PER_TOKEN_RESPONSE,
	AI_BEST_10_WATER_USAGE_PER_TOKEN_RESPONSE,
	AI_CO2_APPS_PERFORMANCE_RESPONSE,
	AI_COMPANY_IMPACT_SUMMARY,
	AI_GENERAL_INFORMATION_RESPONSE,
	AI_MODEL_IMPACT_SUMMARY,
	AI_WATER_APPS_PERFORMANCE_RESPONSE,
	AI_WATER_USAGE_BY_MODEL_SUMMARY,
	EMISSION_RECORDS,
	EXPORT_FILE_RESPONSE,
	FILE_STATUS_RESPONSE,
	GET_EMISSIONS_RESPONSE,
	GET_FILES_RESPONSE
} from "./dashboard-ai.db";

export class DashboardAI {
	public static getFiles(options: HttpRequestOptions) {
		const { pathParams } = options;

		const tenantId = pathParams?.["tenantId"];

		if (!tenantId) {
			console.error("Missing tenantId in path parameters");
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getFiles.name
					)
			);
		}

		return of(GET_FILES_RESPONSE);
	}

	public static uploadFile(options: HttpRequestOptions) {
		const { pathParams, body } = options;

		const tenantId = pathParams?.["tenantId"];

		if (!tenantId || !body) {
			console.error("Missing tenantId path param or body for upload");
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.uploadFile.name
					)
			);
		}

		return of({ fileId: "mock-file-id" });
	}

	public static getFileStatus(options: HttpRequestOptions) {
		const { pathParams } = options;
		const tenantId = pathParams?.["tenantId"];
		const fileId = pathParams?.["fileId"];

		if (!tenantId || !fileId) {
			console.error("Missing tenantId or fileId in path parameters");
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getFileStatus.name
					)
			);
		}

		return of(FILE_STATUS_RESPONSE);
	}

	public static getFileEmissions(options: HttpRequestOptions) {
		const _extractParamValue = (
			params: HttpRequestOptions["params"],
			key: string
		): string | null => {
			if (!params) {
				return null;
			}

			if (params instanceof HttpParams) {
				return params.get(key);
			}

			const rawValue = (params as Record<string, unknown>)[key];

			if (Array.isArray(rawValue)) {
				const first = rawValue[0];
				return first !== undefined ? String(first) : null;
			}

			if (typeof rawValue === "number" || typeof rawValue === "boolean") {
				return String(rawValue);
			}

			return (rawValue as string | undefined) ?? null;
		};

		const { pathParams, params } = options;
		const tenantId = pathParams?.["tenantId"];
		const fileId = pathParams?.["fileId"];
		const pageParam = _extractParamValue(params, "page");
		const sizeParam = _extractParamValue(params, "size");

		if (!tenantId || !fileId || pageParam === null || sizeParam === null) {
			console.error("Missing required pagination or path parameters");
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getFileEmissions.name
					)
			);
		}

		const page = Number(pageParam);
		const size = Number(sizeParam);

		if (Number.isNaN(page) || Number.isNaN(size) || size <= 0) {
			console.error("Invalid pagination parameters", { page, size });
			return throwError(
				() =>
					new ApplicationError(
						"Invalid pagination parameters",
						this.constructor.name,
						this.getFileEmissions.name
					)
			);
		}

		const start = page * size;
		const paginated = EMISSION_RECORDS.slice(start, start + size);
		const totalElements = EMISSION_RECORDS.length;
		const totalPages = Math.max(1, Math.ceil(totalElements / size));

		return of({
			...GET_EMISSIONS_RESPONSE,
			first: page === 0,
			last: page >= totalPages - 1,
			size,
			number: page,
			totalElements,
			totalPages,
			contents: paginated
		});
	}

	public static getGeneralInformation() {
		return of(AI_GENERAL_INFORMATION_RESPONSE);
	}

	public static getCo2AppsPerformance() {
		return of(AI_CO2_APPS_PERFORMANCE_RESPONSE);
	}

	public static getWaterAppsPerformance() {
		return of(AI_WATER_APPS_PERFORMANCE_RESPONSE);
	}

	public static getEnvironmentalImpactByCompany(options: HttpRequestOptions) {
		const body = (options.body as { companies?: string[] } | undefined) ?? {};
		const requestedCompanies = (body.companies || []).filter(Boolean);

		return of(
			DashboardAI._buildEnvironmentalImpactResponse(
				requestedCompanies.length ? requestedCompanies : undefined
			)
		);
	}

	public static getModelEmissions() {
		return of(GET_EMISSIONS_RESPONSE);
	}

	public static renameFile(options: HttpRequestOptions) {
		const { pathParams, body } = options;
		const tenantId = pathParams?.["tenantId"];
		const fileId = pathParams?.["fileId"];
		const name = (body as { name?: string } | undefined)?.name;

		if (!tenantId || !fileId || !name) {
			console.error("Missing required parameters for rename");
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.renameFile.name
					)
			);
		}

		return of(void 0);
	}

	public static deleteFile(options: HttpRequestOptions) {
		const { pathParams } = options;
		const tenantId = pathParams?.["tenantId"];
		const fileId = pathParams?.["fileId"];

		if (!tenantId || !fileId) {
			console.error("Missing required parameters for delete");
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.deleteFile.name
					)
			);
		}

		return of(void 0);
	}

	public static downloadTemplate() {
		return of(new Blob());
	}

	public static exportFile(options: HttpRequestOptions) {
		const { pathParams } = options;
		const tenantId = pathParams?.["tenantId"];
		const fileId = pathParams?.["fileId"];

		if (!tenantId || !fileId) {
			console.error("Missing required parameters for export");
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.exportFile.name
					)
			);
		}

		return of(EXPORT_FILE_RESPONSE);
	}

	public static getEnvironmentalImpactByModel(options: HttpRequestOptions) {
		const body = (options.body as { models?: string[] } | undefined) ?? {};
		const requestedModels = (body.models || []).filter(Boolean);

		return of(
			DashboardAI._buildEnvironmentalImpactByModelResponse(
				requestedModels.length ? requestedModels : undefined
			)
		);
	}

	public static getWaterUsageByModel(options: HttpRequestOptions) {
		const body = (options.body as { models?: string[] } | undefined) ?? {};
		const requestedModels = (body.models || []).filter(Boolean);

		return of(
			DashboardAI._buildWaterUsageByModelResponse(
				requestedModels.length ? requestedModels : undefined
			)
		);
	}

	private static _buildWaterUsageByModelResponse(models?: string[]) {
		const dataset = models?.length
			? AI_WATER_USAGE_BY_MODEL_SUMMARY.filter(entry => models.includes(entry.model))
			: AI_WATER_USAGE_BY_MODEL_SUMMARY;

		if (!dataset.length) {
			return {
				totalWaterUsage: 0,
				waterUnit: "L",
				waterTokenUnit: 1000000,
				pieChart: {
					title: "Water usage by model",
					pieChartSlice: []
				},
				summaryTable: []
			};
		}

		const totalWaterUsage = dataset.reduce((sum, entry) => sum + entry.totalWaterUsage, 0);

		return {
			totalWaterUsage,
			waterUnit: "L",
			waterTokenUnit: 1000000,
			pieChart: {
				title: "Water usage by model",
				pieChartSlice: dataset.map(entry => ({
					name: entry.model,
					value: entry.totalWaterUsage,
					unit: "L",
					percentage: (entry.totalWaterUsage / totalWaterUsage) * 100
				}))
			},
			summaryTable: dataset.map(entry => ({
				model: entry.model,
				company: entry.company,
				totalWaterUsage: entry.totalWaterUsage,
				waterUsagePerTokenUnit: entry.waterUsagePerTokenUnit
			}))
		};
	}

	private static _buildEnvironmentalImpactByModelResponse(models?: string[]) {
		const dataset = models?.length
			? AI_MODEL_IMPACT_SUMMARY.filter(entry => models.includes(entry.model))
			: AI_MODEL_IMPACT_SUMMARY;

		if (!dataset.length) {
			return {
				totalEmission: 0,
				emissionUnit: "kgCO2e",
				emissionTokenUnit: 1000000,
				pieChart: {
					title: "Emissions by model",
					pieChartSlice: []
				},
				summaryTable: []
			};
		}

		const totalEmission = dataset.reduce((sum, entry) => sum + entry.totalEmissions, 0);

		return {
			totalEmission,
			emissionUnit: "kgCO2e",
			emissionTokenUnit: 1000000,
			pieChart: {
				title: "Emissions by model",
				pieChartSlice: dataset.map(entry => ({
					name: entry.model,
					value: entry.totalEmissions,
					unit: "kgCO2e",
					percentage: (entry.totalEmissions / totalEmission) * 100
				}))
			},
			summaryTable: dataset.map(entry => ({
				company: entry.company,
				model: entry.model,
				rowCount: entry.rowCount,
				totalEmissions: entry.totalEmissions,
				emissionPerTokenUnit: entry.emissionPerTokenUnit
			}))
		};
	}

	private static _buildEnvironmentalImpactResponse(companies?: string[]) {
		const dataset = companies?.length
			? AI_COMPANY_IMPACT_SUMMARY.filter(entry => companies.includes(entry.company))
			: AI_COMPANY_IMPACT_SUMMARY;

		if (!dataset.length) {
			return {
				totalEmission: 0,
				pieChart: {
					title: "Emissions by model provider",
					PieChartSlice: []
				},
				summaryTable: []
			};
		}

		const totalEmission = dataset.reduce((sum, entry) => sum + entry.emission, 0);

		return {
			totalEmission,
			pieChart: {
				title: "Emissions by model provider",
				PieChartSlice: dataset.map(entry => ({
					name: entry.company,
					value: entry.emission,
					unit: "kgCO2e"
				}))
			},
			summaryTable: dataset.map(entry => ({
				company: entry.company,
				modelCount: entry.modelCount
			}))
		};
	}

	public static getBest10ModelsByEmission(_options: HttpRequestOptions) {
		return of(AI_BEST_10_EMISSIONS_PER_TOKEN_RESPONSE);
	}

	public static getBest10ModelsByWaterUsage(_options: HttpRequestOptions) {
		return of(AI_BEST_10_WATER_USAGE_PER_TOKEN_RESPONSE);
	}
}
